import { useMemo } from "react";
import { useFeatureFlag } from "@jsq/flags-react";
import { type PermissionRow } from "~/pages/UserAndRoles/WorkspacesPage/types";
import {
  hasManagePositionsPermission,
  isJuniperSquareOperationsManagerRole,
  VIEW_AND_EDIT_POSITIONS_IN_JSQ_ADMINISTERED_ENTITIES_PERMISSION,
} from "../../../../../../utils/userRoles";
import permissionsData from "../../permissions.json";
import { ROLE_GROUP_NAMES, WorkspaceRoleGroupNames } from "./consts";

export const getPermissions = (permissions?: Record<string, boolean>) => {
  // if false for contacts and accounts display as "view contacts/accounts"
  if (!permissions) return [];
  const activePermissions: string[] = Object.keys(permissions).reduce((acc, key) => {
    if (key.includes("View and edit contacts") && !permissions[key]) {
      acc.push("View contacts");
    } else if (key.includes("View and edit accounts") && !permissions[key]) {
      acc.push("View accounts");
    } else if (permissions[key]) {
      acc.push(key);
    }
    return acc;
  }, [] as string[]);

  return activePermissions;
};

export const useFetchWorkspacePermissions = (roleName?: WorkspaceRoleGroupNames) => {
  const faPositionsManagePermFF = useFeatureFlag("faPositionsManagePerm", false);

  const rowData: PermissionRow[] = useMemo(() => {
    if (!roleName) return [];
    const permissionsFromJSON: Record<string, boolean> =
      permissionsData?.[ROLE_GROUP_NAMES[roleName]] ?? null;

    if (!permissionsFromJSON) return [];

    const permissions = { ...permissionsFromJSON };

    // When feature flag is enabled, remove positions permission from specific roles
    if (faPositionsManagePermFF && !hasManagePositionsPermission(roleName)) {
      permissions[VIEW_AND_EDIT_POSITIONS_IN_JSQ_ADMINISTERED_ENTITIES_PERMISSION] = false;
    }

    // Special case for "Juniper Square Operations Manager" role:
    if (isJuniperSquareOperationsManagerRole(roleName)) {
      // In permissions.json, we had configured the permission as false earlier (also the FF off case).
      // When FF is on, we show "View and edit positions in JSQ administered entities" text on the UI.
      permissions[VIEW_AND_EDIT_POSITIONS_IN_JSQ_ADMINISTERED_ENTITIES_PERMISSION] =
        faPositionsManagePermFF;
    }

    const enabledPermissions = getPermissions(permissions);

    return enabledPermissions.map((permission) => ({
      permissionName: permission,
    }));
  }, [roleName, faPositionsManagePermFF]);

  return { rowData };
};
